import { useToast } from '@chakra-ui/toast'
import { STATUS } from 'features/common/status'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { ProjectWorkOrder } from 'types/transaction.type'
import { useClient } from 'utils/auth-context'
import { dateFormat, dateISOFormat, datePickerFormat } from 'utils/date-time-utils'
import { PROJECT_FINANCIAL_OVERVIEW_API_KEY } from './projects'
import { currencyFormatter, removeCurrencyFormat } from 'utils/string-formatters'
import { useTranslation } from 'react-i18next'
import { ACCONT_PAYABLE_API_KEY } from './account-payable'
import { readFileContent } from './vendor-details'
import { sortBy } from 'lodash'
import { useUserRolesSelector } from 'utils/redux-common-selectors'
import { ALL_TICKETS_QUERY_KEY, TICKETS_QUERY_KEY } from './ticket'
import _ from 'lodash'
import numeral from 'numeral'
import { DEFAULT_VENDOR_NOT_ASSIGNED_WO } from 'components/layout/constants'

type UpdateWorkOrderProps = {
  hideToast?: boolean
  swoProjectId?: string | number | null
}

export const WorkOrderStatusOptions = [
  { value: 1020, label: 'NEW' }, // "Active code" 34
  { value: 37, label: 'INACTIVE' },
  { value: 34, label: 'COMPLETED' },
  { value: 36, label: 'CLOSED' },
  { value: 110, label: 'INVOICED' },
  { value: 111, label: 'REJECTED' },
  { value: 1021, label: 'SCHEDULED' },
  { value: 1023, label: 'APPROVED' },
  { value: 1022, label: 'ESTIMATED' },
  { value: 1024, label: 'SUBMITTED' },
  { value: 68, label: 'PAID' },
  { value: 35, label: 'CANCELLED' },
  { value: 1025, label: 'REVISION' },
  { value: 92, label: 'DECLINED' },
]

export const useUpdateWorkOrderMutation = (props: UpdateWorkOrderProps) => {
  const { hideToast } = props
  const client = useClient()
  const toast = useToast()
  const queryClient = useQueryClient()

  const { t } = useTranslation()

  return useMutation(
    payload => {
      return client('work-orders', {
        data: payload,
        method: 'PUT',
      })
    },
    {
      onSuccess(res) {
        const updatedWorkOrderId = res?.data?.id
        const projectId = res?.data?.projectId
        queryClient.invalidateQueries([PROJECT_FINANCIAL_OVERVIEW_API_KEY, projectId])
        queryClient.invalidateQueries(['transactions', projectId])
        queryClient.invalidateQueries(['GetProjectWorkOrders', projectId])
        queryClient.invalidateQueries(['GetProjectWorkOrders', projectId?.toString()])
        queryClient.invalidateQueries(['WorkOrderDetails', updatedWorkOrderId])
        queryClient.invalidateQueries(['project', projectId])
        queryClient.invalidateQueries(['documents', projectId])
        queryClient.invalidateQueries(ACCONT_PAYABLE_API_KEY)
        queryClient.invalidateQueries(ALL_TICKETS_QUERY_KEY)
        queryClient.invalidateQueries(TICKETS_QUERY_KEY)
        queryClient.invalidateQueries(['WorkOrderDetails', updatedWorkOrderId?.toString()])
        queryClient.invalidateQueries(['documents', projectId?.toString()])

        if (!hideToast) {
          toast({
            title: 'Work Order',
            description: 'Work Order has been saved successfully.',
            status: 'success',
            isClosable: true,
            position: 'top-left',
          })
        }
      },
      onError(error: any) {
        let description = error.title ?? 'Unable to save workorder.'
        if (error.errorKey === 'EXPECTED_AND_COMPLETION_1_YEAR_ERROR') {
          description = t('EXPECTED_AND_COMPLETION_1_YEAR_ERROR')
        }
        toast({
          title: 'Work Order',
          description,
          position: 'top-left',
          status: 'error',
          isClosable: true,
        })
      },
    },
  )
}

export const useCreateWorkOrderMutation = () => {
  const client = useClient()
  const toast = useToast()
  const queryClient = useQueryClient()

  const { t } = useTranslation()

  return useMutation(
    payload => {
      return client('work-orders', {
        data: payload,
        method: 'POST',
      })
    },
    {
      onSuccess(res: any) {
        const projectId = res?.data?.projectId

        queryClient.invalidateQueries(['GetProjectWorkOrders', projectId])
        queryClient.invalidateQueries([PROJECT_FINANCIAL_OVERVIEW_API_KEY, projectId])
        queryClient.invalidateQueries(['transactions', projectId])
        queryClient.invalidateQueries(['project', projectId])
        queryClient.invalidateQueries(['documents', projectId])
        queryClient.invalidateQueries(['projectSchedule', projectId])
        queryClient.invalidateQueries(ALL_TICKETS_QUERY_KEY)
        queryClient.invalidateQueries(TICKETS_QUERY_KEY)

        toast({
          title: 'Work Order',
          description: 'Work Order created successfully',
          status: 'success',
          isClosable: true,
          position: 'top-left',
        })
      },
      onError(error: any) {
        let description = error.title ?? 'Unable to save workorder.'
        if (error.errorKey === 'EXPECTED_AND_COMPLETION_1_YEAR_ERROR') {
          description = t('EXPECTED_AND_COMPLETION_1_YEAR_ERROR')
        }
        toast({
          title: 'Work Order',
          position: 'top-left',
          description,
          status: 'error',
          isClosable: true,
        })
      },
    },
  )
}

export const useFetchWorkOrder = ({ workOrderId }: { workOrderId: number | undefined }) => {
  const client = useClient()

  const { data: workOrder, ...rest } = useQuery(
    ['WorkOrderDetails', workOrderId],
    async () => {
      const response = await client(`work-orders/${workOrderId} `, {})
      return response?.data
    },
    {
      enabled: !!workOrderId,
      // refetchOnWindowFocus: true,
    },
  )
  const workOrderDetails = {
    ...workOrder,
    assignedItems: sortBy(workOrder?.assignedItems, e => {
      return e.orderNo
    }),
  }

  return {
    workOrderAssignedItems: sortBy(workOrder?.assignedItems, e => {
      return e.orderNo
    }),
    awardPlanScopeAmount: workOrder?.awardPlanScopeAmount,
    displayAwardPlan: false,
    workOrderDetails: workOrderDetails,
    ...rest,
  }
}

export const useNoteMutation = projectId => {
  const client = useClient()
  const toast = useToast()
  const queryClient = useQueryClient()

  return useMutation(
    (payload: any) => {
      return client('notes', {
        data: payload,
      })
    },
    {
      onSuccess() {
        queryClient.invalidateQueries(['notes', projectId])
        toast({
          title: 'Note',
          description: 'Note has been saved successfully.',
          status: 'success',
          isClosable: true,
          position: 'top-left',
        })
      },
      onError(error: any) {
        toast({
          title: 'Note',
          description: (error.title as string) ?? 'Unable to save note.',
          status: 'error',
          isClosable: true,
          position: 'top-left',
        })
      },
    },
  )
}

export const useNotes = ({ workOrderId }: { workOrderId: number | undefined }) => {
  const client = useClient()

  const { data: notes, ...rest } = useQuery<Array<Document>>(['notes', workOrderId], async () => {
    const response = await client(`notes?workOrderId.equals=${workOrderId}&sort=modifiedDate,asc`, {})
    return response?.data
  })

  return {
    notes,
    ...rest,
  }
}

/* WorkOrder Payments */
export const useFieldEnableDecision = (workOrder?: ProjectWorkOrder) => {
  const { isAdmin, isAccounting } = useUserRolesSelector()
  const defaultStatus = false
  // not used for now -  const completedState = [STATUS.Completed].includes(workOrder?.statusLabel?.toLocaleLowerCase() as STATUS)
  const invoicedState = [STATUS.Invoiced].includes(workOrder?.statusLabel?.toLocaleLowerCase() as STATUS)
  const accountThatHaveAccess = defaultStatus || isAdmin || isAccounting;
  const paymentAndPartialPaymentEnable = !isAdmin || !isAccounting ? defaultStatus || invoicedState : true;
  const invoicedStateOrAccountsThatHaveAccess = invoicedState || accountThatHaveAccess;
  return {
    dateInvoiceSubmittedEnabled: accountThatHaveAccess,
    paymentTermEnabled: invoicedStateOrAccountsThatHaveAccess || (workOrder?.assignAwardPlan && isAdmin),
    paymentTermDateEnabled: accountThatHaveAccess,
    expectedPaymentDateEnabled: accountThatHaveAccess,
    datePaymentProcessedEnabled: invoicedStateOrAccountsThatHaveAccess,
    datePaidEnabled: invoicedStateOrAccountsThatHaveAccess,
    invoiceAmountEnabled: accountThatHaveAccess,
    clientOriginalApprovedAmountEnabled: accountThatHaveAccess,
    clientApprovedAmountEnabled: accountThatHaveAccess,
    finalInvoiceAmountEnabled: defaultStatus,
    paymentDateEnabled: paymentAndPartialPaymentEnable,
    partialPaymentEnabled: paymentAndPartialPaymentEnable,
    //partialPaymentEnabled: false,
  }
}

export const parsePaymentValuesToPayload = formValues => {
  return {
    dateInvoiceSubmitted: dateISOFormat(formValues?.dateInvoiceSubmitted),
    paymentTerm: formValues?.paymentTerm?.value,
    paymentTermDate: dateISOFormat(formValues?.paymentTermDate),
    expectedPaymentDate: formValues?.expectedPaymentDate,
    datePaymentProcessed: dateISOFormat(formValues?.datePaymentProcessed),
    datePaid: dateISOFormat(formValues?.datePaid),
    partialPayment: formValues?.partialPayment,
    invoiceAmount: removeCurrencyFormat(formValues?.invoiceAmount),
    finalInvoiceAmount: removeCurrencyFormat(formValues?.finalInvoiceAmount),
    clientOriginalApprovedAmount: removeCurrencyFormat(formValues?.clientOriginalApprovedAmount),
    clientApprovedAmount: removeCurrencyFormat(formValues?.clientApprovedAmount),
    partialPaymentDate: dateISOFormat(formValues?.paymentDate),
    markAllCompleted: formValues?.markAllCompleted ? true : false
  }
}

export const parseProjectAwardValuesToPayload = (id, projectAwardData) => {
  return {
    awardPlanId: id,
    paymentTerm: projectAwardData.find(pa => pa.id === id)?.payTerm,
  }
}

export const defaultValuesPayment = (workOrder, paymentsTerms) => {
  const defaultValues = {
    dateInvoiceSubmitted: datePickerFormat(workOrder?.dateInvoiceSubmitted),
    paymentTerm: workOrder?.paymentTerm
      ? paymentsTerms.find(p => p.value === workOrder?.paymentTerm)
      : paymentsTerms.find(p => p.value === '20'),
    paymentTermDate: datePickerFormat(workOrder?.paymentTermDate),
    expectedPaymentDate: datePickerFormat(workOrder?.expectedPaymentDate),
    datePaymentProcessed: datePickerFormat(workOrder?.datePaymentProcessed),
    datePaid: datePickerFormat(workOrder?.datePaid),
    invoiceAmount: currencyFormatter(workOrder?.invoiceAmount),
    clientOriginalApprovedAmount: currencyFormatter(workOrder?.clientOriginalApprovedAmount),
    clientApprovedAmount: currencyFormatter(workOrder?.clientApprovedAmount),
    partialPayment: 0,
    // paymentDate:datePickerFormat(workOrder?.partialPaymentDate),
    finalInvoiceAmount: currencyFormatter(workOrder?.finalInvoiceAmount),
  }
  return defaultValues
}

/* WorkOrder Details */

export const useFieldEnableDecisionDetailsTab = ({ workOrder, formValues }) => {
  const { isAdmin, isVendor } = useUserRolesSelector()
  const disableCompleteByVendor = isVendor || [STATUS.New, STATUS.Scheduled].includes(workOrder?.statusLabel?.toLowerCase() as STATUS) || formValues?.assignedItems?.length < 1;
  return {
    disableCompleteByVendor: disableCompleteByVendor,
    disableVerifiedDate: disableCompleteByVendor, // HN-PSWOA-6724| Disable verified and complete by vendor fields if the account is vendor or status is in new or scheduled status
    workOrderStartDateEnable: [STATUS.Active, STATUS.PastDue].includes(workOrder.statusLabel?.toLowerCase()) || isAdmin,
    workOrderExpectedCompletionDateEnable:
      [STATUS.Active, STATUS.PastDue].includes(workOrder.statusLabel?.toLowerCase()) || isAdmin,
  }
}

export const parseWODetailValuesToPayload = formValues => {
  /*- id will be set when line item is saved in workorder
    - smartLineItem id is id of line item in swo */
  const cancelWorkOrder = formValues?.cancel.value === 35

  // const assignedItems = !cancelWorkOrder
  const markAllCompleted = formValues?.assignedItems?.length > 0 && formValues?.assignedItems.every(l => l.isCompleted)
  const markAllVerified =
    formValues?.assignedItems?.length > 0 && formValues?.assignedItems?.every(l => l.isCompleted && l.isVerified)
  const assignedItems = true
    ? [
        ...formValues?.assignedItems?.map((a, index) => {
          const isNewSmartLineItem = !('smartLineItemId' in a)
          if (a.document) {
            delete a?.document?.fileObject
            delete a?.document?.documentTypelabel
          }
          const assignedItem = {
            ...a,
            document: a.uploadedDoc ? { id: a?.document?.id, ...a.uploadedDoc } : a.document,
            id: isNewSmartLineItem ? '' : a.id,
            smartLineItemId: isNewSmartLineItem ? a.id : a.smartLineItemId,
            orderNo: index,
            location: a.location?.value,
          }
          delete assignedItem.uploadedDoc
          return assignedItem
        }),
      ]
    : []
    let status = formValues?.status?.value;
    if ([34, 36].includes(formValues?.status?.value)) {
      if (!markAllVerified) {
        status = 34;
      }
      if (!markAllCompleted) {
        status = 1023;
      }
    }

  return {
    cancel: formValues?.cancel?.value,
    status: status,
    ...(cancelWorkOrder && { status: 35, cancelledDate: new Date() }),
    workOrderStartDate: formValues?.workOrderStartDate,
    workOrderDateCompleted: formValues?.workOrderDateCompleted,
    workOrderDateVerified: formValues?.workOrderDateVerified,
    workOrderExpectedCompletionDate: formValues?.workOrderExpectedCompletionDate,
    showPricing: formValues.showPrice,
    assignedItems: [...assignedItems],
    notifyVendor: formValues.notifyVendor,
    markAllCompleted: markAllCompleted, //formValues?.markAllCompleted,
    markAllVerified: markAllVerified, //formValues?.markAllVerified,
    vendorId: formValues.vendorId?.value ? formValues.vendorId?.value : DEFAULT_VENDOR_NOT_ASSIGNED_WO,
    vendorSkillId: formValues.vendorSkillId?.value,
    workOrderStartTime: formValues?.workOrderTimeSlots?.find(w => w.label === formValues?.workOrderStartTime?.label)
      ?.value, //formValues?.workOrderStartTime?.value,
    finalInvoiceAmount: formValues.finalInvoiceAmount?.value,
    subTotal: formValues.subTotal?.value,
  }
}

export const getUniqueObjectsByProperties = (arr: any, properties: any) => {
  let uniqueObjects: any = []

  arr?.forEach((obj: any) => {
    let isUnique = true

    uniqueObjects.forEach(uniqueObj => {
      let matches = true

      properties.forEach(prop => {
        if (obj[prop] !== uniqueObj[prop]) {
          matches = false
        }
      })

      if (matches) {
        isUnique = false
      }
    })

    if (isUnique) {
      uniqueObjects.push(obj)
    }
  })

  return uniqueObjects
}

export const defaultValuesWODetails = (workOrder, defaultVendor, defaultSkill, locations?) => {
  const workOrderTimeSlots = getUniqueObjectsByProperties(
    workOrder.clientSchedule
      ?.filter(w => w.preferredStartTime?.value)
      .map(w => {
        const item = _.cloneDeep(w)
        return {
          ...item.preferredStartTime,
          label: `${dateFormat(item.preferredStartDate)} ${item.preferredStartTime.label}`,
          dateLabel: `${dateFormat(item.preferredStartDate)}`,
          timeLabel: item.preferredStartTime.label,
        }
      }),
    ['value', 'dateLabel'],
  )

  const defaultValues = {
    cancel: {
      value: '',
      label: 'Select',
    },
    vendorSkillId: defaultSkill,
    vendorId: defaultVendor.value === 100001 ? '' : defaultVendor,
    workOrderStartDate: datePickerFormat(workOrder?.workOrderStartDate),
    workOrderDateCompleted: datePickerFormat(workOrder?.workOrderDateCompleted),
    workOrderDateVerified: datePickerFormat(workOrder?.workOrderDateVerified),
    workOrderExpectedCompletionDate: datePickerFormat(workOrder?.workOrderExpectedCompletionDate),
    showPrice: workOrder.showPricing ?? false,
    notifyVendor: workOrder.notifyVendor ?? false,
    markAllCompleted: workOrder?.markAllCompleted ?? false,
    markAllVerified: workOrder?.markAllVerified ?? false,
    //vendorProfitPercentage: numeral(workOrder.vendorProfitPercentage).format('0.00'),
    vendorProfitPercentage: numeral(
      ((workOrder.clientApprovedAmount - workOrder.subTotal) / workOrder.clientApprovedAmount) * 100,
    ).format('0.00'),
    workOrderNewAmount: numeral(workOrder.workOrderNewAmount).format('0.00'),
    assignedItems:
      workOrder?.assignedItems?.length > 0
        ? workOrder?.assignedItems?.map(e => {
            return {
              ...e,
              uploadedDoc: null,
              clientAmount: (e.price ?? 0) * (e.quantity ?? 0),
              location: locations?.find(l => l.value?.toString() === e.location?.toString()),
            }
          })
        : [],
    workOrderStartTime: workOrderTimeSlots.find(
      w =>
        w.value?.toString() === workOrder.workOrderStartTime?.toString() &&
        w.dateLabel === dateFormat(workOrder?.workOrderStartDate),
    ),
    status: WorkOrderStatusOptions.find(w => w.value === workOrder?.status),
    workOrderTimeSlots: _.cloneDeep(workOrderTimeSlots),
    clientApprovedAmount: numeral(workOrder?.clientApprovedAmount ? workOrder?.clientApprovedAmount : '0').format(
      '0.00',
    ),
    invoiceAmount: workOrder?.invoiceAmount,
    finalInvoiceAmount: workOrder?.finalInvoiceAmount,
    subTotal: workOrder?.subTotal,
  }

  return defaultValues
}

/* WorkOrder LienWaiver */

export const defaultValuesLienWaiver = lienWaiverData => {
  const defaultValues = {
    claimantName: lienWaiverData?.claimantName,
    customerName: lienWaiverData?.customerName,
    propertyAddress: lienWaiverData?.propertyAddress,
    owner: lienWaiverData?.owner,
    makerOfCheck: lienWaiverData?.makerOfCheck,
    finalInvoiceAmount: lienWaiverData?.finalInvoiceAmount,
    checkPayableTo: lienWaiverData?.claimantName,
    claimantsSignature: lienWaiverData?.claimantsSignature,
    claimantTitle: lienWaiverData?.claimantTitle,
    dateOfSignature: lienWaiverData?.dateOfSignature,
    lienWaiverAccepted: !lienWaiverData?.lienWaiverAccepted,
    uploadLW: null as any,
  }
  return defaultValues
}

export const useLWFieldsStatusDecision = ({ workOrder }) => {
  const disabled =
    ![STATUS.Closed, STATUS.Invoiced, STATUS.Rejected].includes(workOrder?.statusLabel?.toLocaleLowerCase()) ||
    (workOrder.leanWaiverSubmitted && workOrder?.lienWaiverAccepted)
  return {
    isFieldsDisabled: disabled,
  }
}

/* New Work Order */

export const parseNewWoValuesToPayload = async (formValues, projectId) => {
  const selectedCapacity = 1
  const markAllCompleted = formValues?.assignedItems?.length > 0 && formValues?.assignedItems.every(l => l.isCompleted)
  const markAllVerified =
    formValues?.assignedItems?.length > 0 && formValues?.assignedItems?.every(l => l.isCompleted && l.isVerified)
  var documents = [] as any
  const assignedItems = [
    ...formValues?.assignedItems?.map((a, index) => {
      if (a.document) {
        delete a?.document?.fileObject
      }
      const assignedItem = {
        ...a,
        document: a.uploadedDoc ? a.uploadedDoc : a.document,
        id: '',
        smartLineItemId: a.id,
        orderNo: index,
        location: a.location?.value,
      }
      delete assignedItem.uploadedDoc
      return assignedItem
    }),
  ]

  if (formValues?.uploadWO) {
    const fileContents = await readFileContent(formValues?.uploadWO)
    documents.push({
      fileObjectContentType: formValues?.uploadWO?.type,
      fileType: formValues?.uploadWO?.name,
      fileObject: fileContents ?? '',
      documentType: 16,
    })
  }
  return {
    cancel: formValues.cancel?.value,
    workOrderStartDate: formValues.workOrderStartDate,
    workOrderExpectedCompletionDate: formValues.workOrderExpectedCompletionDate,
    invoiceAmount: formValues.invoiceAmount,
    clientApprovedAmount: formValues.clientApprovedAmount,
    percentage: formValues.percentage,
    vendorId: formValues.vendorId?.value,
    vendorSkillId: formValues.vendorSkillId?.value,
    // new work-order have hardcoded capacity
    capacity: selectedCapacity,
    assignedItems: formValues?.assignedItems?.length > 0 ? assignedItems : [],
    documents,
    status: 34,
    showPricing: formValues.showPrice,
    notifyVendor: formValues.notifyVendor,
    markAllCompleted: markAllCompleted, //formValues?.markAllCompleted,
    markAllVerified: markAllVerified, //formValues?.markAllVerified,
    projectId: projectId,
  }
}
